import { useDisclosure } from '@mantine/hooks';
import { useEffect, useRef } from 'react';

import { OPEN_SYNC_PROGRESS_EVENT } from '../sync-events';
import { isTerminalSyncPhase, SyncJob } from '../sync-job/types';

export const SYNC_DONE_BANNER_KEY = 'lidea.sync_done_banner';

export function markSyncDoneBanner(): void {
  window.sessionStorage.setItem(SYNC_DONE_BANNER_KEY, '1');
}

export function clearSyncDoneBanner(): void {
  window.sessionStorage.removeItem(SYNC_DONE_BANNER_KEY);
}

export function shouldShowSyncDoneBanner(): boolean {
  return window.sessionStorage.getItem(SYNC_DONE_BANNER_KEY) === '1';
}

type UseSyncProgressModalOptions = {
  isSettingUpCache: boolean;
  job: SyncJob | undefined;
};

export function useSyncProgressModal({
  isSettingUpCache,
  job,
}: UseSyncProgressModalOptions) {
  const [progressOpen, { open: openProgress, close: closeProgress }] =
    useDisclosure(false);
  const wasRunningRef = useRef(false);

  const showProgressModal = () => {
    openProgress();
  };

  const dismissProgressModal = () => {
    closeProgress();
    clearSyncDoneBanner();
  };

  useEffect(() => {
    const onOpenProgress = () => showProgressModal();
    window.addEventListener(OPEN_SYNC_PROGRESS_EVENT, onOpenProgress);
    return () => {
      window.removeEventListener(OPEN_SYNC_PROGRESS_EVENT, onOpenProgress);
    };
  });

  useEffect(() => {
    const justFinished =
      wasRunningRef.current &&
      !isSettingUpCache &&
      job &&
      isTerminalSyncPhase(job.status);

    if (justFinished) {
      markSyncDoneBanner();
    }
    wasRunningRef.current = isSettingUpCache;
  }, [isSettingUpCache, job?.status]);

  return {
    progressOpen,
    showProgressModal,
    dismissProgressModal,
  };
}
